"use client";

import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getRowValue, mergeBulkEdits, parseFromEdit, summarizeBulkRows } from "../../app";
import type { FilterClause } from "../../domain";
import { getColumnName } from "../column-helpers";
import { useTableLabelFn } from "../labels-context";
import { logger } from "../smart-table-logger";

export interface UseRowEditingParams<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  filters: FilterClause[];
  page: number;
  pageSize: number;
  rowIdField: string;
  isRowEditable: ((row: TData) => boolean) | undefined;
  onBulkSave: ((edits: Map<string, Partial<TData>>) => Promise<void>) | undefined;
  onInlineEdit: ((rowId: string, field: string, value: unknown) => Promise<void>) | undefined;
  onSelectedRowsChange: ((rows: TData[]) => void) | undefined;
}

/** Массовое и построчное редактирование: выбор строк, правки, сохранение и отмена. */
export function useRowEditing<TData>({
  columns,
  data,
  filters,
  page,
  pageSize,
  rowIdField,
  isRowEditable: isRowEditableRow,
  onBulkSave,
  onInlineEdit,
  onSelectedRowsChange,
}: UseRowEditingParams<TData>) {
  const t = useTableLabelFn();
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [bulkEdits, setBulkEdits] = useState<Map<string, Partial<TData>>>(
    new Map(),
  );
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [bulkSaveError, setBulkSaveError] = useState<string | null>(null);
  const [inlineEditCell, setInlineEditCell] = useState<{
    rowId: string;
    columnId: string;
    value: string;
  } | null>(null);
  // Bulk "apply-to-all" row: per-column value that applies to every selected
  // row unless that row has an individual edit for the same column.
  const [commonRowValues, setCommonRowValues] = useState<
    Partial<Record<string, unknown>>
  >({});

  const getRowId = useCallback(
    (row: TData): string => {
      const v = getRowValue(row, rowIdField);
      return v !== undefined && v !== null ? String(v) : "";
    },
    [rowIdField],
  );

  const isInlineRowEditable = useCallback(
    (rowId: string): boolean => {
      if (!isRowEditableRow) return true;
      const row = data.find((item) => getRowId(item) === rowId);
      return row !== undefined && isRowEditableRow(row);
    },
    [data, getRowId, isRowEditableRow],
  );

  useEffect(() => {
    if (
      inlineEditCell &&
      isRowEditableRow &&
      !isInlineRowEditable(inlineEditCell.rowId)
    ) {
      setInlineEditCell(null);
    }
  }, [inlineEditCell, isInlineRowEditable, isRowEditableRow]);

  // Single source of truth: columns with meta.editable === true are editable
  // in bulk mode, inline double-click, and Edit modal
  const editableColumns = useMemo(
    () =>
      columns
        .filter((c) => {
          const meta = c.meta;
          return meta?.editable === true;
        })
        .map((c) => getColumnName(c))
        .filter((id): id is string => typeof id === "string"),
    [columns],
  );

  // Summary of effective values across selected rows, per editable column.
  // Used to seed the "apply-to-all" row inputs: "single" when every selected
  // row shares the same value (individual edit ?? original), "mixed" otherwise.
  const commonRowSummary = useMemo(
    () =>
      summarizeBulkRows({ selectedRowIds, data, getRowId, bulkEdits, editableColumns }),
    [selectedRowIds, data, bulkEdits, editableColumns, getRowId],
  );

  useEffect(() => {
    if (!bulkEditMode) return;
    setSelectedRowIds(new Set());
    setBulkEdits(new Map());
    setCommonRowValues({});
    setBulkSaveError(null);
  }, [filters, page, pageSize, bulkEditMode]);

  useEffect(() => {
    if (!onSelectedRowsChange) return;
    if (!bulkEditMode) {
      onSelectedRowsChange([]);
      return;
    }
    const selectedRows = data.filter((r) => selectedRowIds.has(getRowId(r)));
    onSelectedRowsChange(selectedRows);
    const validIds = new Set(data.map(getRowId));
    const staleIds = [...selectedRowIds].filter((id) => !validIds.has(id));
    if (staleIds.length > 0) {
      setSelectedRowIds((prev) => {
        const next = new Set(prev);
        for (const id of staleIds) next.delete(id);
        return next;
      });
    }
  }, [selectedRowIds, data, bulkEditMode, getRowId, onSelectedRowsChange]);

  const toggleRowSelection = useCallback((rowId: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
        setBulkEdits((prevEdits) => {
          if (!prevEdits.has(rowId)) return prevEdits;
          const nextEdits = new Map(prevEdits);
          nextEdits.delete(rowId);
          return nextEdits;
        });
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(
    (visibleRows: TData[]) => {
      setSelectedRowIds((prev) => {
        const allSelected = visibleRows.every((r) => prev.has(getRowId(r)));
        if (allSelected) {
          const next = new Set(prev);
          const removedIds: string[] = [];
          for (const r of visibleRows) {
            const id = getRowId(r);
            next.delete(id);
            removedIds.push(id);
          }
          setBulkEdits((prevEdits) => {
            if (removedIds.every((id) => !prevEdits.has(id))) return prevEdits;
            const nextEdits = new Map(prevEdits);
            for (const id of removedIds) nextEdits.delete(id);
            return nextEdits;
          });
          return next;
        }
        const next = new Set(prev);
        for (const r of visibleRows) next.add(getRowId(r));
        return next;
      });
    },
    [getRowId],
  );

  const setCellEdit = useCallback(
    (rowId: string, field: string, value: unknown) => {
      setBulkEdits((prev) => {
        const next = new Map(prev);
        const existing = next.get(rowId) ?? {};
        next.set(rowId, { ...existing, [field]: value });
        return next;
      });
      setSelectedRowIds((prev) => {
        if (prev.has(rowId)) return prev;
        const next = new Set(prev);
        next.add(rowId);
        return next;
      });
    },
    [],
  );

  const handleBulkSave = useCallback(async () => {
    if (!onBulkSave || selectedRowIds.size === 0) return;
    setIsBulkSaving(true);
    setBulkSaveError(null);
    try {
      const selectedEdits = mergeBulkEdits<TData>(selectedRowIds, bulkEdits, commonRowValues);
      await onBulkSave(selectedEdits);
      setBulkEditMode(false);
      setSelectedRowIds(new Set());
      setBulkEdits(new Map());
      setCommonRowValues({});
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      logger.error(`[SmartTable] bulk save failed: ${message}`);
      setBulkSaveError(t("table.bulkEdit.error"));
      setBulkEdits(new Map());
      setCommonRowValues({});
    } finally {
      setIsBulkSaving(false);
    }
  }, [onBulkSave, selectedRowIds, bulkEdits, commonRowValues]);

  const handleBulkCancel = useCallback(() => {
    setBulkEditMode(false);
    setSelectedRowIds(new Set());
    setBulkEdits(new Map());
    setCommonRowValues({});
    setBulkSaveError(null);
  }, []);

  const handleInlineEditSave = useCallback(
    async (overrideValue?: unknown) => {
      if (!inlineEditCell) {
        setInlineEditCell(null);
        return;
      }
      if (!isInlineRowEditable(inlineEditCell.rowId)) {
        setInlineEditCell(null);
        return;
      }
      const col = columns.find(
        (c) => getColumnName(c) === inlineEditCell.columnId,
      );
      const colMeta = col?.meta;
      const rawValue =
        overrideValue !== undefined ? overrideValue : inlineEditCell.value;
      const parsed = parseFromEdit(
        typeof rawValue === "string" ? rawValue : String(rawValue ?? ""),
        colMeta?.format,
      );
      if (bulkEditMode) {
        setCellEdit(inlineEditCell.rowId, inlineEditCell.columnId, parsed);
      } else if (onInlineEdit) {
        await onInlineEdit(
          inlineEditCell.rowId,
          inlineEditCell.columnId,
          parsed,
        );
      }
      setInlineEditCell(null);
    },
    [
      inlineEditCell,
      onInlineEdit,
      columns,
      bulkEditMode,
      setCellEdit,
      isInlineRowEditable,
    ],
  );

  const handleInlineEditCancel = useCallback(() => {
    setInlineEditCell(null);
  }, []);

  return { bulkEditMode, setBulkEditMode, selectedRowIds, setSelectedRowIds, bulkEdits, setBulkEdits, isBulkSaving, setIsBulkSaving, bulkSaveError, setBulkSaveError, inlineEditCell, setInlineEditCell, commonRowValues, setCommonRowValues, getRowId, isInlineRowEditable, editableColumns, commonRowSummary, toggleRowSelection, toggleAllVisible, setCellEdit, handleBulkSave, handleBulkCancel, handleInlineEditSave, handleInlineEditCancel };
}
